import { BODY_FONT_SIZE } from '@components/Theme/constants';
import {
  gray,
  semanticMap,
  SemanticMapColors,
} from '@components/Theme/newBaseColors';
import { appThemeContract } from '@components/Theme/pallette.css';
import { ComplexStyleRule, globalStyle, style } from '@vanilla-extract/css';

const { neutral } = appThemeContract;

const baseButtonStyles: ComplexStyleRule = {
  borderRadius: '3px',
  cursor: 'pointer',
  justifyContent: 'center',
  padding: '6px 12px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  transition: '.3s',
  fontSize: BODY_FONT_SIZE,
  minHeight: 28,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const boxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.2)';

export const solidStyles = style({
  ...baseButtonStyles,
  border: `1px solid ${semanticMap.primary[40]}`,
  background: semanticMap.primary[40],
  color: gray.white,
  ':hover': {
    border: `1px solid ${semanticMap.primary[30]}`,
    background: semanticMap.primary[30],
    boxShadow,
  },
  ':focus-visible': {
    outlineOffset: '2px !important',
    outline: `2px solid ${semanticMap.primary[40]} !important`,
    transition: 'none',
  },
  ':active': {
    border: `1px solid ${semanticMap.primary[20]}`,
    background: semanticMap.primary[20],
    boxShadow: 'none',
  },
  ':disabled': {
    border: `1px solid ${gray[80]}`,
    background: gray[80],
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

export const outlineStyles = style({
  ...baseButtonStyles,
  background: gray.white,
  color: semanticMap.primary[40],
  border: `1px solid ${semanticMap.primary[40]}`,
  ':focus': {
    color: semanticMap.primary[40],
    background: gray.white,
    border: `1px solid ${semanticMap.primary[40]} !important`,
    transition: 'none',
  },
  ':active': {
    color: semanticMap.primary[10],
    border: `1px solid ${semanticMap.primary[20]} !important`,
    background: semanticMap.primary[80],
    boxShadow: 'none',
  },
  ':disabled': {
    cursor: 'not-allowed !important',
    color: semanticMap.neutral[80],
    border: `1px solid ${semanticMap.neutral[80]}`,
    backgroundColor: gray.white,
    boxShadow: 'none',
  },
  selectors: {
    '&:hover:not(:disabled)': {
      border: `1px solid ${semanticMap.primary[20]}`,
      color: semanticMap.primary[20],
      background: `${semanticMap.primary[90]} !important`,
      boxShadow,
    },
  },
});

const createGhostStyle = (color: SemanticMapColors): string =>
  style({
    ...baseButtonStyles,
    background: gray.white,
    color: semanticMap[color][40],
    border: `1px solid ${gray.white}`,
    minWidth: '25px',
    ':focus': {
      color: semanticMap[color][40],
      background: gray.white,
      border: `1px solid white`,
      transition: 'none',
    },
    ':active': {
      color: semanticMap[color][20],
      border: `1px solid ${semanticMap[color][80]} !important`,
      background: `${semanticMap[color][80]} !important`,
      boxShadow: 'none',
    },
    ':disabled': {
      background: gray.white,
      cursor: 'not-allowed',
      border: `1px solid ${gray.white}`,
      color: gray[80],
      boxShadow: 'none',
    },
    selectors: {
      '&:hover:not(:disabled)': {
        color: semanticMap[color][30],
        background: `${semanticMap[color][90]} !important`,
        border: `1px solid ${semanticMap[color][90]} !important`,
        boxShadow,
      },
    },
  });

export const ghostStyles = createGhostStyle('primary');
export const dangerGhostStyles = createGhostStyle('error');

export const dangerStyles = style({
  ...baseButtonStyles,
  background: semanticMap.error[40],
  color: gray.white,
  ':hover': {
    background: semanticMap.error[30],
    boxShadow,
  },
  ':focus-visible': {
    background: semanticMap.error[40],
    outlineOffset: '2px !important',
    outline: `2px solid ${semanticMap.error[40]} !important`,
    transition: 'none',
  },
  ':active': {
    background: semanticMap.error[20],
    boxShadow: 'none',
  },
  ':disabled': {
    background: gray[80],
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

const createSystemDialogInverseStyle = (color: SemanticMapColors): string =>
  style({
    ...baseButtonStyles,
    background: semanticMap[color][95],
    color: semanticMap[color][40],
    ':hover': {
      background: `${semanticMap[color][90]} !important`,
    },
    ':focus': {
      background: gray.white,
      border: `1px solid white`,
      transition: 'none',
    },
    ':active': {
      border: 'none',
    },
    ':disabled': {
      background: gray.white,
      cursor: 'not-allowed',
      border: `1px solid ${gray.white}`,
      color: gray[80],
      boxShadow: 'none',
    },
  });

export const infoSystemDialogInverseStyle =
  createSystemDialogInverseStyle('primary');
export const errorSystemDialogInverseStyle =
  createSystemDialogInverseStyle('error');
export const warningSystemDialogInverseStyle =
  createSystemDialogInverseStyle('warning');
export const successSystemDialogInverseStyle =
  createSystemDialogInverseStyle('success');

const createSystemDialogStyle = (color: SemanticMapColors): string =>
  style({
    ...baseButtonStyles,
    background: semanticMap[color][90],
    color: semanticMap[color][10],
    ':hover': {
      background: semanticMap[color][90],
      boxShadow,
    },
    ':focus-visible': {
      outlineOffset: '2px !important',
      outline: `2px solid ${semanticMap[color][40]} !important`,
      transition: 'none',
    },
    ':disabled': {
      border: `1px solid ${gray[80]}`,
      background: gray[80],
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  });

export const infoSystemDialogStyle = createSystemDialogStyle('primary');
export const errorSystemDialogStyle = createSystemDialogStyle('error');
export const warningSystemDialogStyle = createSystemDialogStyle('warning');
export const successSystemDialogStyle = createSystemDialogStyle('success');

export const aiStyles = style({
  ...baseButtonStyles,
  // background is a gradient starting at primary going to primary light left to right
  background: `linear-gradient(90deg, ${semanticMap.primary[40]}, ${semanticMap.cyan[70]})`,
  color: neutral[99],
  ':hover': {
    background: `linear-gradient(90deg, ${semanticMap.primary[30]}, ${semanticMap.cyan[60]})`,
    boxShadow,
  },
  ':focus-visible': {
    background: `linear-gradient(90deg, ${semanticMap.primary[40]}, ${semanticMap.cyan[70]})`,
    outlineOffset: '2px !important',
    outline: `2px solid ${semanticMap.primary[40]} !important`,
    transition: 'none',
  },
  ':active': {
    background: `linear-gradient(90deg, ${semanticMap.primary[20]}, ${semanticMap.cyan[50]})`,
    boxShadow: 'none',
  },
  ':disabled': {
    background: gray[80],
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

export const quietStyles = style({
  ...baseButtonStyles,

  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,

  // Consume vars
  background: 'var(--quiet-bg)',
  color: 'var(--quiet-fg)',
  border: '1px solid var(--quiet-border)',

  ':hover': {
    boxShadow,
  },
  ':active': {
    boxShadow: 'none',
  },
  ':disabled': {
    cursor: 'not-allowed',
    boxShadow: 'none',
  },

  /* eslint-disable mastery/named-colors */
  vars: {
    // Light mode defaults
    '--quiet-bg': 'transparent',
    '--quiet-fg': '#1f2937', // readable on light backgrounds
    '--quiet-border': 'transparent',

    '--quiet-hover-bg': '#f3f4f6',
    '--quiet-hover-fg': '#111827',

    '--quiet-active-bg': '#e5e7eb',
    '--quiet-active-fg': '#111827',

    '--quiet-focus': '#60a5fa',
  } as Record<string, string>,

  selectors: {
    '&[hidden]': { display: 'none' },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
    '&:not(:disabled)': { cursor: 'pointer' },
    '&:focus-visible': {
      outline: '2px solid var(--quiet-focus)',
      outlineOffset: 2,
    },
  },
});

// Hover / active (use vars so dark mode can remap too)
globalStyle(`${quietStyles}:not(:disabled):hover`, {
  background: 'var(--quiet-hover-bg)',
  color: 'var(--quiet-hover-fg)',
});

globalStyle(`${quietStyles}:not(:disabled):active`, {
  background: 'var(--quiet-active-bg)',
  color: 'var(--quiet-active-fg)',
});

// Dark mode remap (html.theme-dark)
globalStyle(`html.theme-dark ${quietStyles}`, {
  vars: {
    '--quiet-bg': 'transparent',
    '--quiet-fg': '#e5e7eb', // <-- key fix: readable on dark

    '--quiet-border': 'transparent',

    // Keep "quiet" but visible against dark surfaces
    '--quiet-hover-bg': 'rgba(255, 255, 255, 0.08)',
    '--quiet-hover-fg': '#ffffff',

    '--quiet-active-bg': 'rgba(255, 255, 255, 0.12)',
    '--quiet-active-fg': '#ffffff',

    '--quiet-focus': '#93c5fd',
  } as Record<string, string>,
});

// Dark mode disabled state - match ghost button style
globalStyle(
  `html.theme-dark.button-dark-mode-enabled ${quietStyles}:disabled`,
  {
    background: 'transparent',
    color: gray[40],
    opacity: 1,
  },
);

// ============================================================================
// Dark Mode Button Styles (ME-449354)
// Applied when BOTH theme-dark AND button-dark-mode-enabled classes are present on <html>
// This allows button dark mode to be controlled by the ME-449354 feature flag
// ============================================================================

// Interface for button state styles - using Parameters to extract the type from globalStyle
interface ButtonStateStyles {
  base: Parameters<typeof globalStyle>[1];
  hover?: Parameters<typeof globalStyle>[1];
  focus?: Parameters<typeof globalStyle>[1];
  focusVisible?: Parameters<typeof globalStyle>[1];
  active?: Parameters<typeof globalStyle>[1];
  disabled?: Parameters<typeof globalStyle>[1];
  hoverNotDisabled?: Parameters<typeof globalStyle>[1];
}

// Mapping of state keys to their pseudo-selectors
const stateSelectors = {
  hover: ':hover',
  focus: ':focus',
  focusVisible: ':focus-visible',
  active: ':active',
  disabled: ':disabled',
  hoverNotDisabled: ':hover:not(:disabled)',
} as const;

/**
 * Helper function to apply dark mode styles for a button variant.
 * Styles only apply when BOTH classes are present on <html>:
 * - theme-dark (user's theme preference)
 * - button-dark-mode-enabled (ME-449354 feature flag)
 *
 * @param buttonStyle - The button style class to apply dark mode to
 * @param styles - Object containing base and pseudo-state styles
 */
const applyDarkModeStyles = (
  buttonStyle: string,
  styles: ButtonStateStyles,
): void => {
  // Apply base styles - requires BOTH classes
  globalStyle(
    `html.theme-dark.button-dark-mode-enabled ${buttonStyle}`,
    styles.base,
  );

  // Apply state-specific styles using forEach to avoid repetition
  (Object.keys(stateSelectors) as Array<keyof typeof stateSelectors>).forEach(
    (state) => {
      const stateStyles = styles[state];
      if (stateStyles) {
        globalStyle(
          `html.theme-dark.button-dark-mode-enabled ${buttonStyle}${stateSelectors[state]}`,
          stateStyles,
        );
      }
    },
  );
};

// solidStyles dark mode
applyDarkModeStyles(solidStyles, {
  base: {
    border: `1px solid ${semanticMap.primary[70]}`,
    background: semanticMap.primary[70],
    color: gray[10],
  },
  hover: {
    border: `1px solid ${semanticMap.primary[60]}`,
    background: semanticMap.primary[60],
  },
  focusVisible: {
    outline: `2px solid ${semanticMap.primary[60]} !important`,
  },
  active: {
    color: semanticMap.primary[80],
    border: `1px solid transparent`,
    background: semanticMap.primary[20],
  },
  disabled: {
    color: gray[60],
    background: gray[30],
    border: `1px solid ${gray[30]}`,
  },
});

// outlineStyles dark mode
applyDarkModeStyles(outlineStyles, {
  base: {
    background: 'transparent',
    color: semanticMap.primary[80],
    border: `1px solid ${semanticMap.primary[70]}`,
  },
  hoverNotDisabled: {
    border: `1px solid ${semanticMap.primary[80]}`,
    color: semanticMap.primary[90],
    background: `rgba(255, 255, 255, 0.08) !important`,
  },
  focusVisible: {
    color: semanticMap.primary[80],
    background: 'transparent',
    border: `1px solid ${semanticMap.primary[70]} !important`,
    outline: `1px solid ${semanticMap.primary[70]} !important`,
    outlineOffset: '1px !important',
  },
  active: {
    border: `1px solid ${semanticMap.primary[70]}`,
    background: semanticMap.primary[70],
    color: gray[10],
  },
  disabled: {
    color: gray[40],
    border: `1px solid ${gray[30]}`,
    backgroundColor: 'transparent',
  },
});

// ghostStyles dark mode
applyDarkModeStyles(ghostStyles, {
  base: {
    background: 'transparent',
    color: semanticMap.primary[80],
    border: '1px solid transparent',
  },
  hoverNotDisabled: {
    color: semanticMap.primary[80],
    background: `rgba(255, 255, 255, 0.08) !important`,
    border: `1px solid transparent !important`,
  },
  focusVisible: {
    color: `${semanticMap.primary[80]} !important`,
    background: 'transparent !important',
  },
  active: {
    border: `1px solid ${semanticMap.primary[70]} !important`,
    background: `${semanticMap.primary[70]} !important`,
    color: gray[10],
  },
  disabled: {
    background: 'transparent',
    color: gray[40],
  },
});

// dangerStyles dark mode
applyDarkModeStyles(dangerStyles, {
  base: {
    color: semanticMap.error[30],
    background: semanticMap.error[70],
  },
  hover: {
    color: semanticMap.error[30],
    background: semanticMap.error[60],
  },
  focusVisible: {
    color: semanticMap.error[30],
    background: semanticMap.error[70],
    outline: `2px solid ${semanticMap.error[70]} !important`,
  },
  active: {
    color: semanticMap.error[70],
    background: semanticMap.error[20],
    border: '1px solid transparent',
  },
  disabled: {
    color: gray[60],
    background: gray[30],
  },
});

// dangerGhostStyles dark mode
applyDarkModeStyles(dangerGhostStyles, {
  base: {
    background: 'transparent',
    color: semanticMap.error[80],
    border: '1px solid transparent',
  },
  hoverNotDisabled: {
    color: semanticMap.error[70],
    background: `${semanticMap.error[10]} !important`,
    border: `1px solid ${semanticMap.error[10]} !important`,
  },
  focus: {
    color: semanticMap.error[80],
    background: 'transparent',
    border: `1px solid ${semanticMap.error[70]} !important`,
  },
  active: {
    color: `${semanticMap.error[70]} !important`,
    background: `${semanticMap.error[20]} !important`,
    border: '1px solid transparent !important',
  },
  disabled: {
    background: 'transparent',
    color: gray[40],
  },
});

// aiStyles dark mode
applyDarkModeStyles(aiStyles, {
  base: {
    background: `linear-gradient(90deg, ${semanticMap.primary[60]}, ${semanticMap.cyan[80]})`,
    color: gray[10],
  },
  hover: {
    background: `linear-gradient(90deg, ${semanticMap.primary[50]}, ${semanticMap.cyan[70]})`,
  },
  focusVisible: {
    background: `linear-gradient(90deg, ${semanticMap.primary[60]}, ${semanticMap.cyan[80]})`,
    outline: `2px solid ${semanticMap.primary[60]} !important`,
  },
  active: {
    background: `linear-gradient(90deg, ${semanticMap.primary[20]}, ${semanticMap.cyan[30]})`,
    border: 'none',
  },
  disabled: {
    color: gray[60],
    background: gray[30],
  },
});
/* eslint-enable mastery/named-colors */
